/**
 * Client HTTP dell'app: `fetch` con il cookie di sessione (`credentials: 'include'`).
 * L'app di lei gira sempre con il ruolo `individuo`; il server controlla comunque che
 * corrisponda a quello della sessione, l'header non concede niente da solo.
 *
 * Ogni errore torna nel formato del contratto {error:{code, message_it, remedy_it}}: anche
 * la rete caduta diventa un ApiError leggibile, mai un messaggio tecnico in faccia a lei.
 */
import type { ApiError } from '@mamma/contracts'

export const API_BASE = '/api/v1'

export class ApiFailure extends Error {
  readonly error: ApiError
  readonly status: number

  constructor(error: ApiError, status: number) {
    super(error.message_it)
    this.error = error
    this.status = status
  }
}

const RETE: ApiError = {
  code: 'rete_non_disponibile',
  message_it: 'Non riesco a raggiungere il servizio: la connessione sembra assente.',
  remedy_it: 'Controlli la linea e tocchi Riprova. Quello che ha scritto resta nel campo.',
}

const RISPOSTA_ILLEGGIBILE: ApiError = {
  code: 'risposta_non_leggibile',
  message_it: 'Il servizio ha risposto in un modo che non so leggere.',
  remedy_it: 'Tocchi Riprova. Se succede ancora, lo segnali al supporto tecnico.',
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'X-Role': 'individuo',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiFailure(RETE, 0)
  }

  const testo = await response.text()
  let corpo: unknown = null
  if (testo) {
    try {
      corpo = JSON.parse(testo)
    } catch {
      throw new ApiFailure(RISPOSTA_ILLEGGIBILE, response.status)
    }
  }

  if (!response.ok) {
    const errore = (corpo as { error?: ApiError } | null)?.error
    throw new ApiFailure(errore ?? RISPOSTA_ILLEGGIBILE, response.status)
  }
  return corpo as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body ?? {}),
}
